import React, { useState, useEffect } from "react";
import FormField from "../../../../components/forms/FormField/FormField";
import GeneralUploader from "../../../../components/forms/GeneralUploader/GeneralUploader";
import axios from "../../../../api/axios";
import { addCustomFontRoute, removeCustomFontRoute } from "../../../../api/routes";
import DEBUG_LOGGER from "../../../../helpers/DebugLogger";
import uploadCustomAssetWithConflictResolution from "../../../../helpers/uploadCustomAssetWithConflictResolution";
import { toast } from "react-toastify";
import { IconJsxer } from "../../../../helpers/IconHelper";
import { applyFontPreview, FontPreviewBlock, getCustomFontUrl } from "../../../../helpers/CustomFonts";
import { slideOutPanelControl } from "../SlideoutPanel";
import './CustomFontsContent.scss';

const startsWithLetter = (fontName) => /^[A-Za-z]/.test(fontName);

const CustomFontsContent = (props) => {
    const [newFontName, setNewFontName] = useState('');
    const [newFontNameError, setNewFontNameError] = useState('');
    const [newFontFileData, setNewFontFileData] = useState(null);
    const [newFileError, setNewFileError] = useState('');

    const [editCustomFontIndex, setEditCustomFontIndex] = useState(null);
    const [editingFontName, setEditingFontName] = useState('');
    const [editingFontNameError, setEditingFontNameError] = useState('');
    
    const [confirmRemoveFont, setConfirmRemoveFont] = useState(false);
    const [busy, setBusy] = useState(false);
    const canAddCustomFont = Boolean(newFontName.trim() && startsWithLetter(newFontName) && newFontFileData && !busy);
    const nextUploadStep = !newFontName.trim()
        ? 'Enter a font name to continue'
        : !startsWithLetter(newFontName)
            ? 'Font name must start with a letter'
        : !newFontFileData
            ? 'Select a font file to continue'
            : 'Ready to add this custom font';

    const addCustomFont = () => {
        let gotErrors = false;
        setNewFontNameError('');
        setNewFileError('');

        if (newFontName === '') {
            setNewFontNameError('Please enter a font name.');
            gotErrors = true;
        }
        if (newFontName !== '' && !startsWithLetter(newFontName)) {
            setNewFontNameError('Font name must start with a letter.');
            gotErrors = true;
        }
        if (!newFontFileData) {
            setNewFileError('Please select a font file.');
            gotErrors = true;
        }
        if (newFontName !== '' && newFontName.length > 50) {
            setNewFontNameError('Font name is too long.');
            gotErrors = true;
        } else {
            // is the font name already in use?
            const fontExists = props.customFonts.find(font => font.fontFamily === newFontName);
            if (fontExists) {
                setNewFontNameError('Font name already exists.');
                gotErrors = true;
            }
        }
        if (gotErrors) {
            return;
        }

        // Clear any existing errors since validation passed
        setNewFontNameError('');
        setNewFileError('');
        setBusy(true);

        // Upload the font file
        const formData = new FormData();
        formData.append('filename', newFontFileData.file.name);
        formData.append('campaignID', props.campaignId);
        formData.append('type', 'fonts');
        formData.append('file', newFontFileData.file);
        uploadCustomAssetWithConflictResolution(formData, { withCredentials: true })
                .then(res => {
                    const uploadedFilename = res.data.uploadedFilename?.split('/').pop(); // Get just the filename in case the server returns a path
                    // File successfully uploaded, now add the font to the list
                    axios.post(addCustomFontRoute, {
                        campaignID: props.campaignId,
                        fontFamily: newFontName,
                        fontFilename: uploadedFilename
                    }, { withCredentials: true })
                        .then(res => {
                            toast.success('Font Added Successfully');
                            
                            const newFontData = {
                                fontFamily: newFontName,
                                fontFilename: uploadedFilename
                            };
                            if (props.fullCampaignData) {
                                if (!props.fullCampaignData.customFonts) {
                                    props.fullCampaignData.customFonts = [];
                                }
                                props.fullCampaignData.customFonts.push(newFontData);
                            }

                            // Reset form
                            setNewFontName('');
                            setNewFontFileData(null);
                            setBusy(false);
                            props.onFontAdded?.(newFontName);
                        })
                        .catch(err => {
                            DEBUG_LOGGER.log('Add Font error: ', err);
                            toast.error('Error Adding Font - ' + err);
                            setBusy(false);
                        });
                })
                .catch(err => {
                    DEBUG_LOGGER.log('Upload error: ', err);
                    toast.error('Error Uploading File - ' + err);
                    setBusy(false);
                });
    }

    const editCustomFont = (index) => {
        setEditCustomFontIndex(index);
        const fontData = props.customFonts[index];
        applyFontPreview(fontData.fontFamily, getCustomFontUrl(fontData, props.campaignId), '.apply-edit-custom-font');
        setEditingFontName(fontData.fontFamily);
    }

    const closeEditCustomFont = () => {
        setEditCustomFontIndex(null);
        setEditingFontName('');
        setEditingFontNameError('');
        setNewFontFileData(null);
        setNewFileError('');
    }

    const applyFontChanges = () => {
        let gotErrors = false;
        setEditingFontNameError('');
        setNewFileError('');

        if (editingFontName === '') {
            setEditingFontNameError('Please enter a font name.');
            gotErrors = true;
        }
        if (editingFontName !== '' && !startsWithLetter(editingFontName)) {
            setEditingFontNameError('Font name must start with a letter.');
            gotErrors = true;
        }
        if (editingFontName !== '' && editingFontName.length > 50) {
            setEditingFontNameError('Font name is too long.');
            gotErrors = true;
        } else {
            // is the font name already in use?
            const fontExists = props.customFonts.find(font => font.fontFamily === editingFontName);
            if (fontExists && editingFontName !== props.customFonts[editCustomFontIndex].fontFamily) {
                setEditingFontNameError('Font name already exists.');
                gotErrors = true;
            }
        }
        if (gotErrors) {
            return;
        }

        setBusy(true);
        // Implementation continues...
        // For now just close the edit mode
        closeEditCustomFont();
        setBusy(false);
    }

    const removeFont = () => {
        setConfirmRemoveFont(true);
    }

    const cancelRemoveFont = () => {
        setConfirmRemoveFont(false);
    }

    const removeFontNow = () => {
        const deletedFontFamily = props.customFonts[editCustomFontIndex]?.fontFamily;
        axios.post(removeCustomFontRoute, {
            campaignID: props.campaignId, fontIndex: editCustomFontIndex
        }, { withCredentials: true })
            .then(res => {
                toast.success('Font Removed Successfully');
                
                if (props.fullCampaignData) {
                    if (!props.fullCampaignData.customFonts) {
                        props.fullCampaignData.customFonts = [];
                    }
                    props.fullCampaignData.customFonts.splice(editCustomFontIndex, 1);

                    const resetDeletedFont = (value) => {
                        if (typeof value === 'string') return value === deletedFontFamily ? 'Roboto' : value;
                        if (Array.isArray(value)) return value.map(resetDeletedFont);
                        if (value && typeof value === 'object') {
                            Object.keys(value).forEach((key) => {
                                if (key !== 'customFonts') value[key] = resetDeletedFont(value[key]);
                            });
                        }
                        return value;
                    };
                    resetDeletedFont(props.fullCampaignData);
                }

                window.dispatchEvent(new CustomEvent('custom-font-deleted', {
                    detail: { fontFamily: deletedFontFamily }
                }));

                setBusy(false);
                setEditCustomFontIndex(null);
                setConfirmRemoveFont(false);
            })
            .catch(err => {
                DEBUG_LOGGER.log('Remove Font error: ', err);
                toast.error('Error Removing Font - ' + err);
                setBusy(false);
            });
    }

    if (editCustomFontIndex !== null && confirmRemoveFont) {
        return (
            <div className="custom-fonts-content">
                <div className="confirm-remove">
                    <h3>Remove Font</h3>
                    <p>Are you sure you want to remove "{props.customFonts[editCustomFontIndex]?.fontFamily}"? Any campaign elements using it will be reset to Roboto.</p>
                    <div className="custom-fonts-actions">
                        <button className="standard-button secondary" onClick={cancelRemoveFont}>Cancel</button>
                        <button className="standard-button danger" onClick={removeFontNow}>Remove Font</button>
                    </div>
                </div>
            </div>
        );
    }

    if (editCustomFontIndex !== null) {
        const fontData = props.customFonts[editCustomFontIndex];
        return (
            <div className="custom-fonts-content">
                <div className="edit-font-section">
                    <div className="edit-font-header">
                        <h3>Edit Custom Font</h3>
                        <button className="back-button" onClick={closeEditCustomFont}>← Back to List</button>
                    </div>
                    
                    <div className="current-font-preview">
                        <div className="font-name">{fontData.fontFamily}</div>
                        <div className="font-file light-text">{fontData.fontFilename}</div>
                        <FontPreviewBlock className="apply-edit-custom-font" />
                    </div>

                    <FormField
                        id="editFontName"
                        label="Font Name"
                        type="text"
                        value={editingFontName}
                        setFunc={(val) => setEditingFontName(val)}
                        placeholder="Enter font name"
                        error={editingFontNameError}
                        readonly={busy}
                    />

                    <div className="fl-column no-gap">
                        <GeneralUploader
                            label="Replace Font File (optional)"
                            showLabel={true}
                            uploadImmediately={false}
                            showRemoveOption={false}
                            id="editFontFile"
                            fileSelectedCallback={(fileObj) => setNewFontFileData(fileObj)}
                            acceptedFileTypes=".woff2, .ttf, .otf"
                            enabled={!busy}
                        />
                        <div className="error">{newFileError}</div>
                    </div>

                    <div className="custom-fonts-actions">
                        <button className="standard-button danger" onClick={removeFont}>Remove Font</button>
                        <button className={`standard-button ${busy ? 'disabled' : ''}`} onClick={busy ? null : applyFontChanges}>
                            {busy ? 'Updating...' : 'Update Font'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="custom-fonts-content">
            <div className="custom-fonts-section">
                <h3>Your Custom Fonts</h3>
                <div className="light-text">Custom fonts are added on a campaign level and will be available to other disruptors and features once added.</div>
                
                {!props.customFonts?.length && (
                    <div className="no-fonts-message">No custom fonts have been added yet.</div>
                )}
                
                {props.customFonts?.length > 0 && (
                    <div className="custom-fonts-table-wrap">
                        <table className="custom-fonts-table">
                            <thead>
                                <tr><th>Font Name</th><th>Font File</th><th aria-label="Delete" /></tr>
                            </thead>
                            <tbody>
                                {props.customFonts.map((font, index) => (
                                    <tr key={index} onClick={() => editCustomFont(index)}>
                                        <td>{font.fontFamily}</td>
                                        <td className="font-file">{font.fontFilename}</td>
                                        <td>
                                            <button
                                                type="button"
                                                className="custom-font-delete-button"
                                                aria-label={`Delete ${font.fontFamily}`}
                                                title={`Delete ${font.fontFamily}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEditCustomFontIndex(index);
                                                    setConfirmRemoveFont(true);
                                                }}
                                            >
                                                {IconJsxer.GetIcon(IconJsxer.ICONS.trash, { height: '0.9rem', color: '#ffffff' })}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <hr />

                <h3>Add New Custom Font</h3>

                <div className="add-font-section">
                    <div className="light-text">This will add a custom font to the list of available fonts for customisable elements.</div>
                    <div className="light-text">We recommend using *.woff2 files, but you can also use *.ttf or *.otf.</div>

                    <div className={`custom-font-upload-step ${newFontName.trim() ? 'complete' : 'needs-attention'}`}>
                        <div className="custom-font-upload-step-heading"><span className="step-number">1</span> Name your font <span className="required-badge">Required</span></div>
                        <FormField
                            id="newFontName"
                            label="Font Name"
                            labelNote="Give this font a name before uploading. This is the name you will select in the editor."
                            type="text"
                            value={newFontName}
                            setFunc={(val) => setNewFontName(val)}
                            placeholder="e.g. KOMIKAX"
                            error={newFontNameError}
                            readonly={busy}
                        />
                    </div>
                    
                    <div className={`custom-font-upload-step custom-font-file-required ${newFontFileData ? 'complete' : 'needs-attention'}`}>
                        <div className="custom-font-upload-step-heading"><span className="step-number">2</span> Select font file <span className="required-badge">Required</span></div>
                        <GeneralUploader
                            label="Font File"
                            showLabel={false}
                            uploadImmediately={false}
                            showRemoveOption={false}
                            id="newFontFile"
                            fileSelectedCallback={(fileObj) => {
                                setNewFontFileData(fileObj);
                            }}
                            acceptedFileTypes=".woff2, .ttf, .otf"
                            enabled={!busy}
                        />
                        <div className="error">{newFileError}</div>
                    </div>

                    <div className="custom-fonts-actions">
                        <button 
                            className={`standard-button ${canAddCustomFont ? '' : 'disabled'}`}
                            onClick={canAddCustomFont ? addCustomFont : null}
                            disabled={!canAddCustomFont}
                        >
                            {busy ? 'Adding Font. Please Wait...' : 'Add Custom Font'}
                        </button>
                    </div>
                    <div className={`custom-font-upload-status ${canAddCustomFont ? 'ready' : ''}`}>{nextUploadStep}</div>
                </div>
            </div>
        </div>
    );
}

export default CustomFontsContent;
